import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Smartphone, CheckCircle, XCircle, Clock, RefreshCw, Play } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `K${Number(v || 0).toLocaleString()}`;

/** Admin weekly PawaPay payout summary with a manual run trigger. */
const PawaPayWeeklyReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/pawapay/admin/weekly-report?days=7`);
      const data = await res.json();
      if (data?.success) setReport(data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      await fetch(`${API}/api/pawapay/run-weekly-payouts`, { method: 'POST' });
      await load();
    } catch { /* ignore */ } finally {
      setRunning(false);
    }
  };

  const t = report?.totals || {};

  return (
    <Card className="p-6" data-testid="admin-pawapay-weekly-report">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-emerald-600" /> Weekly Mobile-Money Payouts
          </h3>
          <p className="text-sm text-gray-500">PawaPay · last 7 days</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="report-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={runNow} disabled={running} data-testid="report-run-now-btn">
            <Play className="w-4 h-4 mr-1" /> {running ? 'Running…' : 'Run now'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-xl bg-gray-50 p-3">
          <p className="text-xs text-gray-500">Total payouts</p>
          <p className="text-xl font-bold text-gray-900">{t.count || 0}</p>
          <p className="text-xs text-gray-500">{fmt(t.amount)}</p>
        </div>
        <div className="rounded-xl bg-emerald-50 p-3">
          <p className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Completed</p>
          <p className="text-xl font-bold text-emerald-800">{t.completed_count || 0}</p>
          <p className="text-xs text-emerald-700">{fmt(t.completed_amount)}</p>
        </div>
        <div className="rounded-xl bg-red-50 p-3">
          <p className="text-xs text-red-700 flex items-center gap-1"><XCircle className="w-3 h-3" /> Failed</p>
          <p className="text-xl font-bold text-red-800" data-testid="report-failed-count">{t.failed_count || 0}</p>
          <p className="text-xs text-red-700">{fmt(t.failed_amount)}</p>
        </div>
        <div className="rounded-xl bg-amber-50 p-3">
          <p className="text-xs text-amber-700 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</p>
          <p className="text-xl font-bold text-amber-800">{t.pending_count || 0}</p>
        </div>
      </div>

      {report?.failures?.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-2">Failures needing attention</p>
          <ul className="space-y-1.5">
            {report.failures.map((f) => (
              <li key={f.id} className="flex items-center justify-between text-sm bg-red-50 rounded-lg px-3 py-2" data-testid="report-failure-row">
                <span className="text-gray-700">{f.phoneNumber} · {f.provider === 'TNM_MWI' ? 'TNM' : 'Airtel'}</span>
                <span className="font-semibold text-red-700">{fmt(f.amount)} · {f.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!loading && (!report?.failures || report.failures.length === 0) && (
        <p className="text-sm text-gray-400">No failed payouts in this period. 🎉</p>
      )}
    </Card>
  );
};

export default PawaPayWeeklyReport;
